import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiError } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Download } from "lucide-react";

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.is_admin) return;
    http.get("/admin/feedback")
      .then((r) => setItems(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [user]);

  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  const avg = items?.length
    ? (items.reduce((s, i) => s + (i.usage_likelihood || 0), 0) / items.length).toFixed(1)
    : "–";

  const exportCSV = () => {
    const headers = ["created_at", "clinic_name", "email", "rating", "usage_likelihood", "suggestion"];
    downloadCSV(`patientflow-feedback-${new Date().toISOString().slice(0, 10)}.csv`, headers, items || []);
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-widest">
            <ShieldCheck className="h-4 w-4" /> Admin
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-zinc-900 mt-1">User Feedback</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {items ? `${items.length} responses · avg. likelihood to use ${avg}/10` : "Loading..."}
          </p>
        </div>
        <Button variant="outline" className="gap-2" onClick={exportCSV} disabled={!items?.length} data-testid="export-feedback-csv-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {error && <div className="text-sm text-red-600" data-testid="admin-feedback-error">{error}</div>}

      {items?.length === 0 && (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg p-8 text-center" data-testid="admin-feedback-empty">
          No feedback submitted yet.
        </div>
      )}

      <div className="space-y-3">
        {items?.map((f) => (
          <div key={f.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="feedback-item">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="text-sm font-medium text-zinc-900">{f.clinic_name || "Unknown clinic"}</span>
                <span className="text-xs text-zinc-500 ml-2">{f.email}</span>
              </div>
              <div className="text-xs text-zinc-500">{f.created_at ? new Date(f.created_at).toLocaleString() : ""}</div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <span className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-800 text-xs font-medium capitalize" data-testid="feedback-rating">{f.rating}</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium">
                Likelihood {f.usage_likelihood}/10
              </span>
            </div>
            {f.suggestion && <p className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap">{f.suggestion}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
